/**
 * @typedef {'timeout' | 'network' | 'http' | 'cancelled' | 'unknown'} ApiErrorType
 */

/**
 * @typedef {object} ClientNetworkContext
 * @property {boolean | null} isOnline
 * @property {string} netType
 * @property {number | null} deviceMemory
 * @property {number | null} cpuCores
 * @property {string | null} effectiveType
 * @property {number | null} downlink
 * @property {number | null} rtt
 * @property {DocumentVisibilityState | null} visibilityState
 */

/**
 * @typedef {ClientNetworkContext & {
 *   errorType: ApiErrorType
 *   isTimeout: boolean
 *   axiosCode: string | null
 *   hasResponse: boolean
 *   httpStatus: number | null
 * }} ApiErrorDiagnostics
 */

const getNavigatorConnection = () => {
  if (typeof navigator === 'undefined') return null

  return (
    navigator.connection ||
    navigator.mozConnection ||
    navigator.webkitConnection ||
    null
  )
}

/**
 * wifi / 4g / 3g / unknown — по Network Information API.
 */
export const getNetType = (connection = getNavigatorConnection()) => {
  if (!connection) return 'unknown'

  const { type, effectiveType } = connection

  if (type === 'wifi') return 'wifi'

  if (type === 'cellular' || type === 'wimax') {
    return effectiveType || 'cellular'
  }

  if (effectiveType) return effectiveType

  return 'unknown'
}

/**
 * Снимок сетевого и device-контекста браузера (без SSR).
 * @returns {ClientNetworkContext}
 */
export const getClientNetworkContext = () => {
  if (typeof navigator === 'undefined') {
    return {
      isOnline: null,
      netType: 'unknown',
      deviceMemory: null,
      cpuCores: null,
      effectiveType: null,
      downlink: null,
      rtt: null,
      visibilityState: null,
    }
  }

  const connection = getNavigatorConnection()

  return {
    isOnline: navigator.onLine,
    netType: getNetType(connection),
    deviceMemory: navigator.deviceMemory ?? null,
    cpuCores: navigator.hardwareConcurrency ?? null,
    effectiveType: connection?.effectiveType ?? null,
    downlink: connection?.downlink ?? null,
    rtt: connection?.rtt ?? null,
    visibilityState:
      typeof document !== 'undefined' ? document.visibilityState : null,
  }
}

/**
 * Классификация axios-ошибки для APM и логов.
 * @param {import('axios').AxiosError} error
 * @returns {Pick<ApiErrorDiagnostics, 'errorType' | 'isTimeout' | 'axiosCode' | 'hasResponse' | 'httpStatus'>}
 */
export const classifyAxiosError = (error) => {
  const axiosCode = error?.code ?? null
  const hasResponse = Boolean(error?.response)
  const httpStatus = error?.response?.status ?? null
  const message = error?.message?.toLowerCase() ?? ''
  const errorParams = { axiosCode, hasResponse, httpStatus }

  const isTimeout = axiosCode === 'ECONNABORTED' || message.includes('timeout')

  if (error?.name === 'CanceledError' || axiosCode === 'ERR_CANCELED') {
    return { errorType: 'cancelled', isTimeout: false, ...errorParams }
  }

  if (isTimeout) {
    return { errorType: 'timeout', isTimeout: true, ...errorParams }
  }

  if (
    axiosCode === 'ERR_NETWORK' ||
    (!hasResponse && Boolean(error?.request))
  ) {
    return { errorType: 'network', isTimeout: false, ...errorParams }
  }

  if (hasResponse) {
    return { errorType: 'http', isTimeout: false, ...errorParams }
  }

  return { errorType: 'unknown', isTimeout: false, ...errorParams }
}

/**
 * Полная диагностика для сетевой ошибки API.
 * @param {import('axios').AxiosError} error
 * @returns {ApiErrorDiagnostics}
 */
export const getApiErrorDiagnostics = (error) => ({
  ...getClientNetworkContext(),
  ...classifyAxiosError(error),
})

/**
 * Лейблы для Elastic APM (string | number | boolean).
 * @param {ApiErrorDiagnostics} diagnostics
 */
export const getApmErrorLabels = (diagnostics) => ({
  isOnline: diagnostics.isOnline ?? false,
  netType: String(diagnostics.netType),
  isTimeout: diagnostics.isTimeout,
  deviceMemory: diagnostics.deviceMemory ?? 0,
  cpuCores: diagnostics.cpuCores ?? 0,
  httpStatus: diagnostics.httpStatus ?? 0,
  errorType: String(diagnostics.errorType),
})
